import { Badge } from '@/components/ui/Badge/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FieldComponentProps } from './types';

// FIXME: temp field for legacy lists, delete when possible and use labels

const TagsView = ({ value }: { value?: string[] | null }) => {
  if (!value || value.length === 0) {
    return <p className="text-muted-foreground">-</p>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {value.map((tag) => (
        <Badge key={tag} variant="secondary" className="text-xs">
          {tag}
        </Badge>
      ))}
    </div>
  );
};

export const TagsField: React.FC<FieldComponentProps> = ({
  formMode,
  controllerField,
  field,
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const tags: string[] = useMemo(
    () => controllerField.value ?? [],
    [controllerField.value],
  );

  const addTag = useCallback(
    (tag: string) => {
      const trimmed = tag.trim();
      if (trimmed && !tags.includes(trimmed)) {
        controllerField.onChange([...tags, trimmed]);
      }
      setInputValue('');
    },
    [tags, controllerField],
  );

  const removeTag = useCallback(
    (tag: string) => {
      controllerField.onChange(tags.filter((t) => t !== tag));
    },
    [tags, controllerField],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        addTag(inputValue);
      } else if (e.key === 'Backspace' && inputValue === '' && tags.length) {
        removeTag(tags[tags.length - 1]);
      }
    },
    [inputValue, tags, addTag, removeTag],
  );

  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      addTag(inputValue);
    }
  }, [inputValue, addTag]);

  if (formMode === 'loading') {
    return <Skeleton className="h-8 w-full" />;
  }

  if (formMode !== 'edit' || field.meta.readOnly) {
    return <TagsView value={tags} />;
  }

  return (
    <div
      className={cn(
        `border-input bg-background flex min-h-8 flex-wrap items-center gap-1 rounded-md
        border px-2 py-1`,
        'focus-within:ring-ring focus-within:ring-1',
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map((tag) => (
        <Badge key={tag} variant="secondary" className="gap-0.5 text-xs">
          {tag}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeTag(tag);
            }}
            className="hover:text-foreground ml-0.5 rounded-full"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={
          tags.length === 0 ? (field.meta.placeholder ?? 'Add tags...') : ''
        }
        className="h-6 min-w-[80px] flex-1 border-0 p-0 text-sm shadow-none focus-visible:ring-0"
      />
    </div>
  );
};
